import React, { useState, useCallback } from "react";
import { useToast } from "@chakra-ui/react";
import { ApolloError, useApolloClient } from "@apollo/client";
import { keyBy } from "lodash";
import { DocumentType, gql } from "__generated__/gql";
import { useCurrentBrandData } from "utils/currentUserHooks";
import { formatUpc } from "components/Shared/Forms/UpcInput";
import ExportMenuItem from "components/Shared/ExportMenuItem";
import {
  TWO_DECIMALS,
  onlyStringValue,
  currencyTwoDecimalsColumn,
  integerColumn,
  makeFormattedDecimalColumn,
  INCHES,
  POUNDS,
  XlsxExportColumnFormatParams,
} from "utils/xlsxUtils";
import ToastError from "utils/ToastError";
import asyncVoidHandler from "utils/asyncVoidHandler";
import type { ValidatedData } from "utils/xlsxImportExport/XlsxImportWorkBook";
import {
  ColumnFormatsByColumnBySheet,
  getWorkBookExport,
} from "utils/xlsxImportExport/export";
import {
  getWeightUnitExcelFormat,
  WEIGHT_UNIT_LABELS,
} from "components/Shared/weightUnit";
import { WeightUnit } from "__generated__/gql/graphql";
import { SKU_COMPONENT_TYPES } from "components/Products/SkuPanel/SkuComponents/utils";
import {
  ProductLineRow,
  SkuRow,
  WorkSheetDefinitions,
  WORKSHEET_DEFINITIONS,
  skuComponentUpcFields,
  SkuComponentUpcFields,
  skuComponentGrossWeightFields,
  SkuComponentGrossWeightFields,
  skuComponentDimensionFields,
  SkuComponentDimensionFields,
  SkuComponentFields,
  skuComponentFieldPrefixByType,
} from "./Import/utils";
import type { ProductFilterQueryVariables } from "./utils";

const query = gql(/* GraphQL */ `
  query ExportProductInformation(
    $brandId: UUID!
    $productLineFilter: ProductLineFilter
    $skuFilter: SkuFilter
  ) {
    productLinesList(
      condition: { brandId: $brandId }
      filter: $productLineFilter
      orderBy: NAME_ASC
    ) {
      id
      name
      shortName
      nameSeparator
      shipsFromZip
      innerCaseQuantity
      wholesaleCaseQuantity
      wholesaleUnitPrice
      skus: skusList(filter: $skuFilter, orderBy: NAME_ASC) {
        id
        name
        shortName
        unitNetWeight
        unitNetWeightUnit
        unitCogs
        unitSrp
        internalSkuId
        grossPalletWeightLbs
        skuComponentsList {
          id
          skuComponentType
          upc
          grossWeightLbs
          dimensions {
            id
            height
            width
            depth
          }
        }
        tiHi {
          id
          ti
          hi
        }
      }
    }
  }
`);

type SkuColumnFormatsForFields<F extends keyof SkuRow> = Record<
  F,
  XlsxExportColumnFormatParams<SkuRow>
>;

const columnFormats: ColumnFormatsByColumnBySheet<WorkSheetDefinitions> = {
  productLines: {
    name: { columnInfo: { wch: 25 } },
    shortName: { columnInfo: { wch: 20 } },
    nameSeparator: { columnInfo: { wch: 5 } },
    shipsFromZip: { columnInfo: { wch: 14 } },
    innerCaseQuantity: {
      ...integerColumn,
      columnInfo: { wch: 23 },
    },
    wholesaleCaseQuantity: {
      ...integerColumn,
      columnInfo: { wch: 23 },
    },
    wholesaleUnitPrice: {
      ...currencyTwoDecimalsColumn,
      columnInfo: { wch: 20 },
    },
  },
  skus: {
    productLineName: { columnInfo: { wch: 25 } },
    name: { columnInfo: { wch: 25 } },
    shortName: { columnInfo: { wch: 20 } },
    internalSkuId: { columnInfo: { wch: 25 } },
    ...skuComponentUpcFields.reduce((acc, f) => {
      acc[f] = {
        columnInfo: { wch: 20 },
        parseValue: onlyStringValue(formatUpc),
      };
      return acc;
    }, {} as SkuColumnFormatsForFields<SkuComponentUpcFields>),
    unitNetWeight: {
      columnFormat: TWO_DECIMALS,
      columnInfo: { wch: 20 },
      parseValue: onlyStringValue(parseFloat),
      processCell: (cell, { row }) => {
        if (!cell || !row.unitNetWeightUnit) return;
        const format = getWeightUnitExcelFormat(row.unitNetWeightUnit);
        if (!format) return;
        // eslint-disable-next-line no-param-reassign
        cell.z = format;
      },
    },
    unitNetWeightUnit: {
      parseValue: (value) => WEIGHT_UNIT_LABELS[value as WeightUnit],
    },
    ...skuComponentGrossWeightFields.reduce((acc, f) => {
      acc[f] = makeFormattedDecimalColumn(POUNDS);
      return acc;
    }, {} as SkuColumnFormatsForFields<SkuComponentGrossWeightFields>),
    grossPalletWeightLbs: makeFormattedDecimalColumn(POUNDS),
    unitCogs: currencyTwoDecimalsColumn,
    unitSrp: currencyTwoDecimalsColumn,
    ...skuComponentDimensionFields.reduce((acc, f) => {
      acc[f] = makeFormattedDecimalColumn(INCHES);
      return acc;
    }, {} as SkuColumnFormatsForFields<SkuComponentDimensionFields>),
    ti: integerColumn,
    hi: integerColumn,
  },
};

type ProductLinesDatum = NonNullable<
  DocumentType<typeof query>["productLinesList"]
>[number];

function getSheetData(
  productLinesData: readonly ProductLinesDatum[],
): ValidatedData<WorkSheetDefinitions> {
  const skus: SkuRow[] = [];
  const productLines = productLinesData.map<ProductLineRow>((pl) => {
    pl.skus.forEach((plSku) => {
      const { skuComponentsList, tiHi, ...skuData } = plSku;
      const skuComponentsByType = keyBy(skuComponentsList, "skuComponentType");
      const skuComponentFields = SKU_COMPONENT_TYPES.reduce((acc, sct) => {
        const skuComponent = skuComponentsByType[sct];
        if (!skuComponent) return acc;

        const prefix = skuComponentFieldPrefixByType[sct];

        acc[`${prefix}Upc`] = skuComponent.upc;
        acc[`${prefix}GrossWeightLbs`] = skuComponent.grossWeightLbs;

        if (skuComponent.dimensions) {
          acc[`${prefix}DimensionHeight`] = skuComponent.dimensions.height;
          acc[`${prefix}DimensionWidth`] = skuComponent.dimensions.width;
          acc[`${prefix}DimensionDepth`] = skuComponent.dimensions.depth;
        }

        return acc;
      }, {} as Pick<SkuRow, SkuComponentFields>);
      skus.push({
        productLineName: pl.name,
        ...skuComponentFields,
        ti: tiHi?.ti,
        hi: tiHi?.hi,
        ...skuData,
      });
    });
    return pl;
  });
  return { productLines, skus };
}

type Props = ProductFilterQueryVariables;

function ExportProductInformationButton({
  productLineFilter,
  skuFilter,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);

  const client = useApolloClient();
  const brandData = useCurrentBrandData();
  const toast = useToast();

  const handleClick = useCallback(async () => {
    setIsLoading(true);

    try {
      const { default: xlsx } = await import("xlsx");

      const { data } = await client.query({
        query,
        variables: brandData?.id
          ? { brandId: brandData.id, productLineFilter, skuFilter }
          : undefined,
        fetchPolicy: "network-only",
      });

      if (!data?.productLinesList) {
        throw new Error("Unexpected response returned from query.");
      }

      if (!data.productLinesList.length) {
        throw new ToastError("No product lines were found to export.");
      }

      const workSheetsDataByKey = getSheetData(data.productLinesList);

      const workBook = await getWorkBookExport(
        WORKSHEET_DEFINITIONS,
        workSheetsDataByKey,
        columnFormats,
      );

      xlsx.writeFile(
        workBook,
        `Product Information for ${brandData?.name || ""}.xlsx`,
      );
    } catch (e) {
      if (e instanceof ToastError) {
        toast(e.toastOptions);
      } else if (e instanceof ApolloError) {
        toast({
          title: "An error occurred fetching the export data",
          description: e.message,
        });
      } else {
        throw e;
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    brandData?.name,
    brandData?.id,
    client,
    toast,
    productLineFilter,
    skuFilter,
  ]);

  return (
    <ExportMenuItem
      onClick={asyncVoidHandler(handleClick)}
      isLoading={isLoading}
    >
      Product Information
    </ExportMenuItem>
  );
}

export default ExportProductInformationButton;
